import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";
import { format, subHours, subDays } from "date-fns";
import { VehicleTypeData, VehicleTypes } from "../types";
import "./CapacityChart.css";

interface CapacityChartProps {
  data: VehicleTypeData[];
  vehicleTypes: VehicleTypes;
  loading: boolean;
  error: string | null;
  onTimeRangeChange: (
    startTime: string,
    endTime: string,
    vehicleTypeId?: string
  ) => void;
}

const CapacityChart: React.FC<CapacityChartProps> = ({
  data,
  vehicleTypes,
  loading,
  error,
  onTimeRangeChange
}) => {
  const [timeRange, setTimeRange] = useState("24h");
  const [customStartTime, setCustomStartTime] = useState("");
  const [customEndTime, setCustomEndTime] = useState("");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string>("all");

  const handleTimeRangeChange = useCallback((range: string) => {
    setTimeRange(range);
    setShowCustomRange(range === "custom");
  }, []);

  const handleCustomTimeSubmit = useCallback(() => {
    if (customStartTime && customEndTime) {
      const vehicleTypeId =
        selectedVehicleType === "all" ? undefined : selectedVehicleType;
      onTimeRangeChange(customStartTime, customEndTime, vehicleTypeId);
    }
  }, [customStartTime, customEndTime, selectedVehicleType, onTimeRangeChange]);

  useEffect(() => {
    const now = new Date();
    let startTime: Date;
    let endTime = now;

    switch (timeRange) {
      case "1h":
        startTime = subHours(now, 1);
        break;
      case "6h":
        startTime = subHours(now, 6);
        break;
      case "24h":
        startTime = subHours(now, 24);
        break;
      case "7d":
        startTime = subDays(now, 7);
        break;
      case "30d":
        startTime = subDays(now, 30);
        break;
      case "custom":
        if (customStartTime && customEndTime) {
          startTime = new Date(customStartTime);
          endTime = new Date(customEndTime);
        } else {
          startTime = subHours(now, 24);
        }
        break;
      default:
        startTime = subHours(now, 24);
    }

    const vehicleTypeId =
      selectedVehicleType === "all" ? undefined : selectedVehicleType;
    onTimeRangeChange(
      startTime.toISOString(),
      endTime.toISOString(),
      vehicleTypeId
    );
  }, [
    timeRange,
    customStartTime,
    customEndTime,
    selectedVehicleType,
    onTimeRangeChange
  ]);

  // Format data for the chart - memoized to prevent unnecessary re-renders
  const chartData = useMemo(() => {
    // Group data by timestamp and vehicle type
    const groupedData: { [key: string]: { [vehicleTypeId: string]: number } } =
      {};

    data.forEach(item => {
      const timestamp = item.timestamp;
      if (!groupedData[timestamp]) {
        groupedData[timestamp] = {};
      }
      groupedData[timestamp][item.vehicle_type_id] = item.count;
    });

    // Convert to chart format
    return Object.entries(groupedData).map(([timestamp, vehicleCounts]) => {
      const chartItem: any = {
        timestamp: new Date(timestamp),
        formattedTime: format(new Date(timestamp), "MMM dd, HH:mm")
      };

      // Add each vehicle type as a separate data key
      Object.entries(vehicleCounts).forEach(([vehicleTypeId, count]) => {
        const vehicleTypeName =
          vehicleTypes[vehicleTypeId] || `Type ${vehicleTypeId}`;
        chartItem[vehicleTypeName] = count;
      });

      return chartItem;
    });
  }, [data, vehicleTypes]);

  // Calculate statistics - memoized to prevent unnecessary re-renders
  const stats = useMemo(() => {
    const allCounts = data.map(d => d.count);
    const maxCount = Math.max(...allCounts, 0);
    const minCount = Math.min(...allCounts, 0);
    const avgCount =
      data.length > 0
        ? Math.round(data.reduce((sum, d) => sum + d.count, 0) / data.length)
        : 0;

    return { maxCount, minCount, avgCount };
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-time">
            {format(new Date(label), "MMM dd, yyyy HH:mm:ss")}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="tooltip-capacity">
              {entry.dataKey}:{" "}
              <span className="capacity-value">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="capacity-chart">
        <div className="loading">Loading capacity data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="capacity-chart">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="capacity-chart">
      <div className="chart-controls">
        <div className="time-range-selector">
          <label>Time Range:</label>
          <select
            value={timeRange}
            onChange={e => handleTimeRangeChange(e.target.value)}
            className="time-select"
          >
            <option value="1h">Last Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        <div className="vehicle-type-selector">
          <label>Vehicle Type:</label>
          <select
            value={selectedVehicleType}
            onChange={e => setSelectedVehicleType(e.target.value)}
            className="vehicle-type-select"
          >
            <option value="all">All Types</option>
            {Object.entries(vehicleTypes).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {showCustomRange && (
          <div className="custom-range">
            <div className="custom-inputs">
              <div className="input-group">
                <label>Start Time:</label>
                <input
                  type="datetime-local"
                  value={customStartTime}
                  onChange={e => setCustomStartTime(e.target.value)}
                />
              </div>
              <div className="input-group">
                <label>End Time:</label>
                <input
                  type="datetime-local"
                  value={customEndTime}
                  onChange={e => setCustomEndTime(e.target.value)}
                />
              </div>
              <button onClick={handleCustomTimeSubmit} className="apply-button">
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="chart-stats">
        <div className="stat">
          <span className="stat-label">Max:</span>
          <span className="stat-value">{stats.maxCount}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Min:</span>
          <span className="stat-value">{stats.minCount}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Avg:</span>
          <span className="stat-value">{stats.avgCount}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Data Points:</span>
          <span className="stat-value">{data.length}</span>
        </div>
      </div>

      <div className="chart-container">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={time => format(new Date(time), "HH:mm")}
              />
              <YAxis
                domain={[0, "dataMax + 5"]}
                label={{
                  value: "Count",
                  angle: -90,
                  position: "insideLeft"
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              {(() => {
                // Get unique vehicle types from the data
                const uniqueVehicleTypes = Array.from(
                  new Set(data.map(item => item.vehicle_type_id))
                );

                return uniqueVehicleTypes.map((vehicleTypeId, index) => {
                  const vehicleTypeName =
                    vehicleTypes[vehicleTypeId] || `Type ${vehicleTypeId}`;
                  const colors = [
                    "#2563eb",
                    "#dc2626",
                    "#059669",
                    "#7c3aed",
                    "#ea580c"
                  ];
                  const color = colors[index % colors.length];
                  return (
                    <Line
                      key={vehicleTypeId}
                      type="monotone"
                      dataKey={vehicleTypeName}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ fill: color, strokeWidth: 1, r: 2 }}
                      activeDot={{ r: 4, stroke: color, strokeWidth: 2 }}
                    />
                  );
                });
              })()}
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="2 2" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data">
            <p>No availability data available for the selected time range.</p>
            <p>
              Data is collected every minute, so it may take some time to
              accumulate.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapacityChart;
